import { ArrowLeft, MoreHorizontal } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Chip } from '@/components/ui/chip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Surface } from '@/components/ui/surface'
import { useConfirm } from '@/components/patterns/useConfirm'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { toastFailure } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { todayIso } from '../dates'
import { CrmApiError } from '../http'
import { shortDay, weekdayDay } from '../record/format'
import { confirmCapture, deleteCapture, patchCapture, processAndWait, undoCapture } from './api'
import { MentionResolver } from './MentionResolver'
import { ProposalCard } from './ProposalCard'
import {
  ACTIVITY_LABEL,
  changeLines,
  checkedCount,
  decisionsFor,
  effectiveChecks,
  isEmptyResult,
  mentionOutcome,
  proposalRefs,
  waitingMention,
} from './reviewModel'
import { SaveAsNote } from './SaveAsNote'
import { ActivitySheet } from './sheets/ActivitySheet'
import { AgreementChangeSheet } from './sheets/AgreementChangeSheet'
import { NewAgreementSheet } from './sheets/NewAgreementSheet'
import { TaskSheet } from './sheets/TaskSheet'
import {
  captureContext,
  type ActivityProposal,
  type AgreementChange,
  type ConfirmBody,
  type ConfirmResponse,
  type Mention,
  type NewAgreement,
  type TaskProposal,
  type VoiceCapture,
} from './types'

const NO_AGREEMENT = 'Sparky wist niet welke afspraak je bedoelt. Kies er een met Bewerken.'
const NO_AGREEMENT_AT_ALL = 'Deze relatie heeft geen lopende afspraak om te wijzigen.'

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">{title}</h2>
      {children}
    </section>
  )
}

function mentionLabel(mention: Mention): string {
  const chosen = mention.chosen
  if (!chosen || chosen.type === 'geen') return mention.name
  return chosen.organisation_name
    ? `${chosen.name ?? mention.name} · ${chosen.organisation_name}`
    : (chosen.name ?? mention.name)
}

function dayLabel(iso: string): string {
  return iso === todayIso() ? 'vandaag' : weekdayDay(iso)
}

type Editing = { ref: string; kind: 'activity' | 'task' | 'change' | 'new' }

/**
 * The review screen (addendum B.4.1): who was meant, one card per proposal with a tick, and one
 * sticky [Vastleggen (n)]. Nothing is written before that; unticked proposals count as rejected.
 */
export function ReviewScreen({
  capture,
  onCapture,
  onConfirmed,
}: {
  capture: VoiceCapture
  onCapture: (capture: VoiceCapture) => void
  onConfirmed: (response: ConfirmResponse) => void
}) {
  const navigate = useNavigate()
  const { membersById } = useTeamMembers()
  const [confirmDialog, askConfirm] = useConfirm()
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>({})
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({})
  const [editing, setEditing] = useState<Editing | null>(null)
  const [resolving, setResolving] = useState<string | null>(null)
  const [transcriptOpen, setTranscriptOpen] = useState(false)
  const [includeTranscript, setIncludeTranscript] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const result = capture.result
  const context = captureContext(capture)

  async function commit(body: ConfirmBody) {
    setBusy(true)
    try {
      const response = await confirmCapture(capture.id, body)
      const refs = response.refs
      const parts = [
        counted((refs.activities ?? []).filter((item) => item.type !== 'note').length, 'gesprek', 'gesprekken'),
        counted((refs.activities ?? []).filter((item) => item.type === 'note').length, 'notitie', 'notities'),
        counted(refs.tasks?.length ?? 0, 'taak', 'taken'),
        counted((refs.agreement_changes?.length ?? 0) + (refs.new_agreements?.length ?? 0), 'afspraak', 'afspraken'),
      ].filter(Boolean)
      toastUndo(`Vastgelegd${parts.length ? `: ${parts.join(', ')}` : ''}`, async () => {
        try {
          onCapture(await undoCapture(capture.id))
          toast.success('Ongedaan gemaakt.')
        } catch (caught) {
          if (caught instanceof CrmApiError && caught.code === 'undo_conflict') {
            const lines = caught.body?.conflicts
            toast.error(caught.message, {
              description: Array.isArray(lines) ? lines.map(String).join(' ') : undefined,
            })
            return
          }
          throw caught
        }
      })
      onConfirmed(response)
    } catch (caught) {
      if (caught instanceof CrmApiError && caught.code === 'already_confirmed' && caught.body) {
        const refs = (caught.body.refs ?? {}) as ConfirmResponse['refs']
        onConfirmed({ refs, undo_until: String(caught.body.undo_until ?? '') })
        return
      }
      const proposals = caught instanceof CrmApiError ? caught.body?.proposals : null
      if (Array.isArray(proposals) && proposals.length > 0) {
        const next: Record<string, string> = {}
        for (const item of proposals as { ref: string; message: string }[]) next[item.ref] = item.message
        setCardErrors(next)
        toast.error('Niet alles kon worden vastgelegd. Er is niets vastgelegd.', {
          description: 'Pas de gemarkeerde voorstellen aan of vink ze uit.',
        })
        return
      }
      toastFailure(caught, 'Vastleggen mislukt.')
    } finally {
      setBusy(false)
    }
  }

  async function discard() {
    const ok = await askConfirm({
      title: 'Verslag weggooien?',
      description: 'De ingesproken tekst wordt direct gewist.',
      confirmLabel: 'Weggooien',
    })
    if (!ok) return
    try {
      await deleteCapture(capture.id)
      navigate('/relaties/inspreken', { replace: true })
    } catch (caught) {
      toastFailure(caught, 'Weggooien mislukt.')
    }
  }

  if (capture.status === 'mislukt' || !result) {
    return (
      <Shell>
        {confirmDialog}
        <Surface variant="page" padding="md" className="space-y-3">
          <h2 className="text-lg font-semibold">Verwerken is niet gelukt</h2>
          <p className="text-base text-muted-foreground">
            {capture.error_message ?? 'Sparky kon dit nu niet verwerken.'} Je tekst is bewaard.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                try {
                  onCapture(await processAndWait(capture.id))
                } catch (caught) {
                  toastFailure(caught, 'Opnieuw proberen is niet gelukt.')
                } finally {
                  setBusy(false)
                }
              }}
            >
              Opnieuw proberen
            </Button>
            <SaveAsNote
              label="Zonder AI als notitie bewaren bij … ▾"
              context={context}
              disabled={busy}
              onSave={(target) => void commit({ fallback_note: target })}
            />
          </div>
        </Surface>
      </Shell>
    )
  }

  if (isEmptyResult(result)) {
    return (
      <Shell>
        {confirmDialog}
        <Surface variant="page" padding="md" className="space-y-3">
          <h2 className="text-lg font-semibold">Hier kon Sparky niets mee</h2>
          <p className="text-base text-muted-foreground">
            Er staan geen taken, afspraken of verslagen in. Pas de tekst aan, bewaar hem als notitie of gooi hem weg.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                try {
                  onCapture(
                    await patchCapture(capture.id, {
                      transcript: capture.transcript ?? '',
                      input_method: capture.input_method ?? 'toetsenbord',
                    }),
                  )
                } catch (caught) {
                  toastFailure(caught, 'De tekst kon niet worden geopend.')
                } finally {
                  setBusy(false)
                }
              }}
            >
              Tekst aanpassen
            </Button>
            <SaveAsNote
              label="Als notitie bewaren bij … ▾"
              context={context}
              disabled={busy}
              onSave={(target) => void commit({ fallback_note: target })}
            />
            <Button variant="outline" disabled={busy} onClick={() => void discard()}>
              Weggooien
            </Button>
          </div>
        </Surface>
      </Shell>
    )
  }

  const checks = effectiveChecks(result, touched, edits)
  const count = checkedCount(checks)
  const waiting = waitingMention(result)
  const reason = waiting
    ? `Kies eerst bij Wie de juiste ${waiting.name}`
    : count === 0
      ? 'Vink minstens één voorstel aan.'
      : null
  const refsInfo = new Map(proposalRefs(result, edits).map((item) => [item.ref, item]))
  const mentionByRef = new Map(result.mentions.map((mention) => [mention.ref, mention]))
  const merged = <T extends { ref: string }>(item: T): T => ({
    ...item,
    ...(edits[item.ref] ?? {}),
  })
  const ownerName = (id: string | null | undefined, text?: string | null) =>
    (id ? membersById?.get(id)?.display_name : null) ?? text ?? null
  const relationOf = (item: { blocked_by: string[] }) =>
    item.blocked_by.length > 0 ? (mentionByRef.get(item.blocked_by[0] ?? '')?.name ?? null) : null

  const clearError = (ref: string) =>
    setCardErrors((current) => {
      if (!(ref in current)) return current
      return Object.fromEntries(Object.entries(current).filter(([key]) => key !== ref))
    })
  const tick = (ref: string) => (value: boolean) => {
    clearError(ref)
    setTouched((current) => ({ ...current, [ref]: value }))
  }
  const waitText = (blocked: string[]) => (blocked.length > 0 ? 'Wacht op je keuze bij Wie.' : undefined)
  const flagsOf = (item: { flags: string[]; link_flags: string[] }) => [...item.flags, ...item.link_flags]

  const activities = result.activities.map(merged)
  const tasks = result.tasks.map(merged)
  const changes = result.agreement_changes.map(merged).filter((item) => item.superseded_by == null)
  const newAgreements = result.new_agreements.map(merged)
  const activityLabel =
    new Set(activities.map((item) => item.type)).size === 1 && activities[0]
      ? ACTIVITY_LABEL[activities[0].type].toUpperCase()
      : 'VERSLAG'

  const renderActivity = (item: ActivityProposal) => {
    const blocked = item.blocked_by.length > 0
    const met = item.with_refs.map((ref) => mentionByRef.get(ref)).filter((m): m is Mention => m !== undefined)
    const flags = flagsOf(item)
    if (item.relation_missing && !blocked) flags.push('Een gesprek moet bij een relatie horen.')
    return (
      <ProposalCard
        key={item.ref}
        label={item.title ?? ACTIVITY_LABEL[item.type]}
        checked={checks[item.ref] === true}
        onCheckedChange={tick(item.ref)}
        waitingText={waitText(item.blocked_by)}
        flags={blocked ? [] : flags}
        error={cardErrors[item.ref]}
        onEdit={blocked ? undefined : () => setEditing({ ref: item.ref, kind: 'activity' })}
      >
        <p className="flex flex-wrap items-center gap-2 text-base font-semibold">
          {ACTIVITY_LABEL[item.type]}
          <Chip color="grijs">{dayLabel(item.occurred_on)}</Chip>
        </p>
        {item.title ? <p className="text-base">{item.title}</p> : null}
        {!blocked && met.length > 0 ? (
          <p className="text-sm text-muted-foreground">{`Met ${met.map(mentionLabel).join(', ')}`}</p>
        ) : null}
        {!blocked && item.summary ? <p className="line-clamp-3 text-sm">{item.summary}</p> : null}
        {!blocked && item.contact_rhythm_effect ? (
          <p className="text-sm text-muted-foreground">{`ⓘ ${item.contact_rhythm_effect}`}</p>
        ) : null}
      </ProposalCard>
    )
  }

  const renderTask = (item: TaskProposal) => {
    const owner = ownerName(item.owner_user_id, item.owner_text)
    return (
      <ProposalCard
        key={item.ref}
        label={item.title}
        checked={checks[item.ref] === true}
        onCheckedChange={tick(item.ref)}
        waitingText={waitText(item.blocked_by)}
        flags={item.blocked_by.length > 0 ? [] : flagsOf(item)}
        error={cardErrors[item.ref]}
        onEdit={item.blocked_by.length > 0 ? undefined : () => setEditing({ ref: item.ref, kind: 'task' })}
      >
        <p className="text-base font-semibold">{item.title}</p>
        {item.blocked_by.length === 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <Chip color="grijs">{owner ?? 'Kies een eigenaar'}</Chip>
            <Chip color="grijs">{item.due_on ? weekdayDay(item.due_on) : 'geen datum'}</Chip>
            {item.deadline_text ? (
              <span className="text-sm text-muted-foreground">{`'${item.deadline_text}'`}</span>
            ) : null}
          </div>
        ) : null}
        {item.similar_task && item.blocked_by.length === 0 ? (
          <p className="text-sm text-muted-foreground">{`Lijkt op bestaande taak '${item.similar_task.title}'`}</p>
        ) : null}
      </ProposalCard>
    )
  }

  const renderChange = (item: AgreementChange) => {
    const info = refsInfo.get(item.ref)
    const noAgreement = !item.agreement_id && item.blocked_by.length === 0
    const lines = changeLines(item.change, shortDay)
    const relinked = item.agreement_id !== result.agreement_changes.find((c) => c.ref === item.ref)?.agreement_id
    const preview = relinked ? null : item.preview
    return (
      <ProposalCard
        key={item.ref}
        label={item.agreement_title ?? item.agreement_text ?? 'Afspraakwijziging'}
        checked={checks[item.ref] === true}
        onCheckedChange={tick(item.ref)}
        waitingText={
          waitText(item.blocked_by) ??
          (noAgreement ? (item.candidates.length > 0 ? NO_AGREEMENT : NO_AGREEMENT_AT_ALL) : undefined)
        }
        flags={info?.blocked ? [] : flagsOf(item)}
        error={cardErrors[item.ref]}
        onEdit={info?.blocked ? undefined : () => setEditing({ ref: item.ref, kind: 'change' })}
      >
        <p className="text-base font-semibold">{item.agreement_title ?? item.agreement_text ?? 'Afspraak'}</p>
        {item.blocked_by.length === 0 ? (
          <>
            {lines.map((line) => (
              <p key={line} className="text-sm">
                {line}
              </p>
            ))}
            {preview && preview.deadlines[0] ? (
              <p className="text-sm text-muted-foreground">
                {`Volgende: uiterlijk ${shortDay(preview.deadlines[0])}${preview.task_from ? ` (taak vanaf ${shortDay(preview.task_from)})` : ''}`}
              </p>
            ) : null}
            {preview?.warnings.map((warning) => (
              <p key={warning} className="text-sm text-muted-foreground">
                {warning}
              </p>
            ))}
            {item.quote ? <p className="text-sm text-muted-foreground">{`"${item.quote}"`}</p> : null}
          </>
        ) : null}
      </ProposalCard>
    )
  }

  const renderNew = (item: NewAgreement) => {
    const preview = item.preview
    return (
      <ProposalCard
        key={item.ref}
        label={item.title}
        checked={checks[item.ref] === true}
        onCheckedChange={tick(item.ref)}
        waitingText={waitText(item.blocked_by)}
        flags={item.blocked_by.length > 0 ? [] : flagsOf(item)}
        error={cardErrors[item.ref]}
        onEdit={item.blocked_by.length > 0 ? undefined : () => setEditing({ ref: item.ref, kind: 'new' })}
      >
        <p className="text-base font-semibold">{item.title}</p>
        {item.blocked_by.length === 0 ? (
          <>
            <p className="text-sm">Nieuwe afspraak</p>
            {preview && preview.deadlines[0] ? (
              <p className="text-sm text-muted-foreground">{`Eerste deadline ${shortDay(preview.deadlines[0])}`}</p>
            ) : null}
            {preview?.warnings.map((warning) => (
              <p key={warning} className="text-sm text-muted-foreground">
                {warning}
              </p>
            ))}
            {item.quote ? <p className="text-sm text-muted-foreground">{`"${item.quote}"`}</p> : null}
          </>
        ) : null}
      </ProposalCard>
    )
  }

  const editedActivity = editing?.kind === 'activity' ? activities.find((a) => a.ref === editing.ref) : undefined
  const editedTask = editing?.kind === 'task' ? tasks.find((a) => a.ref === editing.ref) : undefined
  const editedChange = editing?.kind === 'change' ? changes.find((a) => a.ref === editing.ref) : undefined
  const editedNew = editing?.kind === 'new' ? newAgreements.find((a) => a.ref === editing.ref) : undefined
  const saveEdit = (values: Record<string, unknown>) => {
    if (editing) {
      clearError(editing.ref)
      setEdits((current) => ({
        ...current,
        [editing.ref]: { ...(current[editing.ref] ?? {}), ...values },
      }))
    }
    setEditing(null)
  }
  const resolvingMention = resolving ? mentionByRef.get(resolving) : undefined
  const noTasksOrChanges = tasks.length === 0 && changes.length === 0 && newAgreements.length === 0

  return (
    <Shell
      menu={
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="icon" aria-label="Meer opties">
              <MoreHorizontal aria-hidden />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-2">
            <label className="flex min-h-11 cursor-pointer items-center gap-3 px-2 text-sm">
              <Checkbox checked={includeTranscript} onChange={(event) => setIncludeTranscript(event.target.checked)} />
              Ingesproken tekst opnemen in het verslag
            </label>
            <button
              type="button"
              className="flex min-h-11 w-full items-center px-2 text-left text-sm hover:bg-muted"
              onClick={() => {
                setMenuOpen(false)
                void discard()
              }}
            >
              Verslag weggooien
            </button>
          </PopoverContent>
        </Popover>
      }
    >
      {confirmDialog}
      {capture.transcript ? (
        <div>
          <button
            type="button"
            aria-expanded={transcriptOpen}
            className="inline-flex min-h-11 items-center text-sm font-medium text-primary"
            onClick={() => setTranscriptOpen((open) => !open)}
          >
            {`Ingesproken tekst ${transcriptOpen ? '▾' : '▸'}`}
          </button>
          {transcriptOpen ? <p className="mt-1 text-base whitespace-pre-wrap">{capture.transcript}</p> : null}
        </div>
      ) : null}

      {result.mentions.length > 0 ? (
        <Block title="Wie">
          <ul className="space-y-2">
            {result.mentions.map((mention) => {
              const open = !mention.chosen && (mention.certainty === 'kiezen' || mention.certainty === 'niet_gevonden')
              return (
                <li key={mention.ref} className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-base">{mentionOutcome(mention)}</p>
                    {mention.basis && mention.chosen ? (
                      <p className="text-sm text-muted-foreground">{mention.basis}</p>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant={open ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setResolving(mention.ref)}
                  >
                    {open ? 'Kiezen' : 'Wijzigen'}
                  </Button>
                </li>
              )
            })}
          </ul>
        </Block>
      ) : null}

      {activities.length > 0 ? <Block title={activityLabel}>{activities.map(renderActivity)}</Block> : null}
      {noTasksOrChanges ? (
        <p className="text-base text-muted-foreground">Geen taken of afspraakwijzigingen gevonden.</p>
      ) : null}
      {tasks.length > 0 ? <Block title={`Taken (${tasks.length})`}>{tasks.map(renderTask)}</Block> : null}
      {changes.length + newAgreements.length > 0 ? (
        <Block title={`Afspraken (${changes.length + newAgreements.length})`}>
          {changes.map(renderChange)}
          {newAgreements.map(renderNew)}
        </Block>
      ) : null}
      <Block title="Niet geplaatst">
        {result.unplaced.length === 0 ? (
          <p className="text-base text-muted-foreground">Geen.</p>
        ) : (
          <ul className="list-disc space-y-1 pl-5 text-base">
            {result.unplaced.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </Block>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background p-3 md:static md:border-0 md:bg-transparent md:p-0">
        {reason ? (
          <p id="vastleggen-reden" className="pb-1 text-sm text-muted-foreground">
            {reason}
          </p>
        ) : null}
        <div className="flex gap-2">
          <Button
            size="lg"
            className="flex-1 md:flex-none"
            disabled={reason !== null || busy}
            aria-describedby={reason ? 'vastleggen-reden' : undefined}
            onClick={() =>
              void commit({
                decisions: decisionsFor(result, checks, edits),
                include_transcript: includeTranscript,
              })
            }
          >
            {`Vastleggen (${count})`}
          </Button>
          <Button size="lg" variant="outline" onClick={() => navigate(returnPath(capture))}>
            Later
          </Button>
        </div>
      </div>

      {editedActivity ? (
        <ActivitySheet
          activity={editedActivity}
          withText={
            editedActivity.with_refs
              .map((ref) => mentionByRef.get(ref))
              .filter((m): m is Mention => m !== undefined)
              .map(mentionLabel)
              .join(', ') || null
          }
          onSave={saveEdit}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {editedTask ? (
        <TaskSheet
          task={editedTask}
          relationName={relationOf(editedTask) ?? null}
          onSave={saveEdit}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {editedChange ? (
        <AgreementChangeSheet change={editedChange} onSave={saveEdit} onClose={() => setEditing(null)} />
      ) : null}
      {editedNew ? (
        <NewAgreementSheet agreement={editedNew} onSave={saveEdit} onClose={() => setEditing(null)} />
      ) : null}
      {resolvingMention ? (
        <MentionResolver
          key={resolvingMention.ref}
          captureId={capture.id}
          mention={resolvingMention}
          open
          onClose={() => setResolving(null)}
          onResolved={(next) => {
            // A new result (after a choice under "Wie") drops the old per-card messages.
            setCardErrors({})
            onCapture(next)
          }}
        />
      ) : null}
    </Shell>
  )
}

function returnPath(capture: VoiceCapture): string {
  const context = captureContext(capture)
  if (!context) return '/relaties/overzicht'
  return context.type === 'contact' ? `/relaties/contacten/${context.id}` : `/relaties/organisaties/${context.id}`
}

function counted(n: number, one: string, many: string): string | null {
  return n === 0 ? null : `${n} ${n === 1 ? one : many}`
}

function Shell({ menu, children }: { menu?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-5 pb-28 md:pb-0">
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/relaties/inspreken"
          className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          <ArrowLeft aria-hidden className="size-4" />
          Inspreken
        </Link>
        {menu}
      </div>
      <h1 className="font-heading text-2xl font-bold">Controleren</h1>
      {children}
    </div>
  )
}
